import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { AuthService } from '../service/auth.service';
import { switchMap, catchError } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class AuthGuard {
  private _authService = inject(AuthService);
  private _router = inject(Router);

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Can activate
   *
   * @param route
   * @param state
   */
  canActivate(): Observable<boolean> {
    return this.checkAuth();
  }

  canActivateChild(): Observable<boolean> {
    return this.checkAuth();
  }

  private checkAuth(): Observable<boolean> {
    // 1. Vérifie la présence du token
    if (!this._authService.getToken()) {
      this.redirect();
      return of(false);
    }

    // 2. Vérifie l'authentification
    return this._authService.isAuthenticated().pipe(
      switchMap((authenticated) => {
        // 3. Si authentifié → accès permis
        if (authenticated) return of(true);

        // 4. Si non authentifié mais a un refreshToken → tente de rafraîchir
        if (this._authService.getRefreshToken()) {
          return this.handleTokenRefresh();
        }

        // 5. Sinon → redirection login
        this.redirect();
        return of(false);
      }),
      catchError(() => {
        this.redirect();
        return of(false);
      }),
    );
  }

  private handleTokenRefresh(): Observable<boolean> {
    return this._authService.refreshToken().pipe(
      switchMap(({ token }) => {
        this._authService.saveToken(token);
        // Re-vérifie l'authentification après rafraîchissement
        return this._authService.isAuthenticated();
      }),
      catchError(() => {
        this.redirect();
        return of(false);
      }),
    );
  }

  private redirect(): void {
    this._router.navigate(['/login']);
  }
}
